import {
  Box,
  Button,
  IconButton,
  Stack,
  Step,
  StepLabel,
  stepLabelClasses,
  Stepper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import { useStyles } from "./style";
import { useEffect, useMemo, useRef, useState } from "react";
import PickServiceAndStaff from "./components/PickServiceAndStaff";
import PickDayAndTime from "./components/PickDayAndTime";
import Confirm from "./components/Confirm";
import moment, { Moment } from "moment-timezone";
import { GUEST_INFO, SERVICES_RESCHEDULE, USER_INFO } from "common/constant";
import authServices from "servers/auth";
import appointmentServices from "servers/appointment";
import {
  ICategories,
  IStaffs,
  ISelectedService,
  IBookingSettings,
  IRescheduleServices,
} from "./interface";
import {
  calcUnixDateTime,
  isValidEmail,
  isValidPhoneNumber,
} from "common/utils";
import { CheckIcon } from "svgr/check";
import { CalendarCheckIcon } from "svgr/calendarCheck";
import ServicesInformation from "./components/ServicesInformation";
import RICHLoading from "components/animate/RICHLoading";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { CalendarNotification } from "svgr/calendarNotification";

const ColorlibStepLabel = styled(StepLabel)(() => ({
  [`& .${stepLabelClasses.label}`]: {
    [`&.${stepLabelClasses.completed}`]: {
      background: "linear-gradient(to right, #DD4C23, #F27C1E)",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontWeight: 500,
      opacity: 1,
    },
    [`&.${stepLabelClasses.active}`]: {
      "& .MuiTypography-root": {
        color: "#EE4725 !important",
      },
      opacity: 1,
    },
    marginTop: "5px !important",
    opacity: 0.6,
    "& .MuiTypography-root": {
      fontSize: "14px !important",
      color: "#092540",
    },
  },
}));

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 9px)",
    right: "calc(50% + 9px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient(to right, #DD4C23, #F27C1E)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient(to right, #DD4C23, #F27C1E)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#DAE0F1",
    borderRadius: 1,

    ...theme.applyStyles("dark", {
      borderColor: theme.palette.grey[800],
    }),
  },
}));

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { active?: boolean };
}>(({ theme }) => ({
  color: "#eaeaf0",
  display: "flex",
  width: 20,
  height: 20,
  "& .ColorlibStepIcon-completedIcon": {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(to right, #DD4C23, #F27C1E) padding-box, linear-gradient(to right, #DD4C23, #F27C1E) border-box",
    borderRadius: "50%",
    padding: 0,
  },
  "& .ColorlibStepIcon-activeIcon": {
    position: "relative",
    flex: 1,
    borderRadius: "50%",
    background:
      "linear-gradient(#fff 0 0) padding-box, linear-gradient(to right, #DD4C23, #F27C1E) border-box",
    border: "2px solid transparent",
    zIndex: 1,
    "&::before": {
      content: '""',
      position: "absolute",
      width: 10,
      height: 10,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      borderRadius: "50%",
      background: "linear-gradient(to right, #DD4C23, #F27C1E)",
      zIndex: 2,
    },
  },
  "& .ColorlibStepIcon-circle": {
    flex: 1,
    borderRadius: "50%",
    border: "2px solid #D1D5DB",
  },
  ...theme.applyStyles("dark", {
    color: theme.palette.grey[700],
  }),
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className, onClick } = props;
  return (
    <ColorlibStepIconRoot
      ownerState={{ active }}
      className={className}
      onClick={onClick}
      sx={{ cursor: completed ? "pointer" : "" }}
    >
      {completed ? (
        <IconButton className="ColorlibStepIcon-completedIcon">
          <CheckIcon />
        </IconButton>
      ) : active ? (
        <div className="ColorlibStepIcon-activeIcon" />
      ) : (
        <div className="ColorlibStepIcon-circle" />
      )}
    </ColorlibStepIconRoot>
  );
}

const steps = ["Service / Staff", "Date & Time", "Confirm"];

const Booking = () => {
  const tabContentRef = useRef<any>(null);
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [bookingSettings, setBookingSettings] = useState<IBookingSettings>({
    confirmRequired: false,
    includeBufferTime: true,
    maxAdvanceDays: 60,
    allowMultipleServices: true,
    onlySingleStaffMember: false,
    onlyShowAnyoneOption: false,
    showBookForSomeoneElse: false,
    showServicePrice: true,
  });

  const [categories, setCategories] = useState<ICategories>([]);
  const [staffs, setStaffs] = useState<IStaffs[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedServices, setSelectedServices] = useState<
    Array<ISelectedService>
  >([]);
  const [selectedDateTime, setSelectedDateTime] = useState<any>({
    date: undefined,
    time: "",
  });
  const userPhone = sessionStorage.getItem(USER_INFO);
  const [bookingForm, setBookingForm] = useState({
    submitting: false,
    submitted: false,
    data: {
      name: "",
      phone: userPhone || "",
      email: "",
      comments: "",
      firstName: "", // It is use for case booking for someone else
      lastName: "",
      birthday: null as Moment | null,
    },
  });

  const [isValidForm, setIsValidForm] = useState<boolean>(false);
  const [customerInfo, setCustomerInfo] = useState<any>(null);
  const [isBusinessWorkHoursOff, setIsBusinessWorkHoursOff] = useState(false);
  const [serviceStep, setServiceStep] = useState<number>(0);

  const handleSwitchTab = (tabIndex: number) => {
    if (serviceStep === 1 && activeTab === 0) {
      setServiceStep(0);
      const _currentServiceIdx = selectedServices.length - 1;
      const _currentService = selectedServices[_currentServiceIdx];
      setSelectedServices(
        selectedServices.filter(
          (service, idx) =>
            !(
              service.serviceId === _currentService.serviceId &&
              idx === _currentServiceIdx
            )
        )
      );
    } else tabIndex < activeTab && setActiveTab(tabIndex);
  };

  const handleBackToMenu = () => {
    setActiveTab(0);
    setServiceStep(0);
    setSelectedServices([]);
    setSelectedDateTime({
      date: undefined,
      time: "",
    });
    setBookingForm({
      submitting: false,
      submitted: false,
      data: {
        name: "",
        phone: "",
        email: "",
        comments: "",
        firstName: "",
        lastName: "",
        birthday: null,
      },
    });
    sessionStorage.removeItem(USER_INFO);
    sessionStorage.removeItem(SERVICES_RESCHEDULE);
    setCustomerInfo(null);
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const authResponse = await authServices.getTokens();
      if (authResponse.data.success) {
        sessionStorage.setItem(
          GUEST_INFO,
          JSON.stringify(authResponse.data.data)
        );
      }
      const appointmentResponse = await appointmentServices.getData();
      if (appointmentResponse.data.success) {
        if (appointmentResponse.data.data.isActive === false)
          window.location.replace("https://richpaymentsolutions.com/agents/");
        if (appointmentResponse.data.data.isBusinessWorkHoursOff === true) {
          setTenantName(appointmentResponse.data.data.businessName);
          setIsBusinessWorkHoursOff(true);
        } else {
          const {
            tenantName,
            timezone,
            ownerNeedsToConfirmAppointment,
            maxAdvanceBookingDays,
            entireBufferTime,
            categories,
            allowMultipleServices,
            onlyAllowSingleStaffMember,
            onlyShowAnyoneOption,
            showBookForSomeoneElse,
            showServicePrice,
          } = appointmentResponse.data.data;
          setTenantName(tenantName);
          setTimezone(timezone);
          // Store booking settings
          setBookingSettings({
            confirmRequired: ownerNeedsToConfirmAppointment,
            includeBufferTime: entireBufferTime,
            maxAdvanceDays: maxAdvanceBookingDays,
            allowMultipleServices,
            onlySingleStaffMember: onlyAllowSingleStaffMember,
            onlyShowAnyoneOption,
            showBookForSomeoneElse,
            showServicePrice,
          });

          setCategories(
            (categories as ICategories)
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((category) => ({
                ...category,
                services: category.services.sort(
                  (a, b) => a.orderIndex - b.orderIndex
                ),
              }))
          );
          const allStaffs = (categories as ICategories).reduce(
            (acc: IStaffs[], category) => {
              category.services.forEach((service) => {
                if ((service.staffs || []).length > 0) {
                  acc = acc.concat(
                    (service.staffs || []).map((staff) =>
                      staff.role === "Owner"
                        ? { ...staff, staffName: "Anyone" }
                        : staff
                    )
                  );
                }
              });
              return acc;
            },
            []
          );
          setStaffs(allStaffs);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const unixPickDateTime = useMemo(() => {
    return calcUnixDateTime(
      selectedDateTime.date,
      selectedDateTime.time,
      timezone
    );
  }, [JSON.stringify(selectedDateTime), timezone]);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (timezone) {
      moment.tz.setDefault(timezone);
    } else {
      moment.tz.setDefault();
    }
  }, [timezone]);

  useEffect(() => {
    tabContentRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeTab]);

  useEffect(() => {
    const validPhone = isValidPhoneNumber(bookingForm.data.phone);
    const validName = !!bookingForm.data.name.trim();
    const validEmail =
      !bookingForm.data.email || isValidEmail(bookingForm.data.email);

    const validForm = validPhone && validName && validEmail;
    setIsValidForm(validForm);
  }, [JSON.stringify(bookingForm)]);

  useEffect(() => {
    setBookingForm({
      ...bookingForm,
      data: { ...bookingForm.data, phone: userPhone || "" },
    });
  }, [userPhone]);

  useEffect(() => {
    if (!categories.length || !staffs.length) return;

    const servicesReschedule = JSON.parse(
      sessionStorage.getItem(SERVICES_RESCHEDULE) || "[]"
    );

    if (Array.isArray(servicesReschedule) && servicesReschedule.length > 0) {
      const allServices = categories.flatMap((category) => category.services);

      const _selectedServices: ISelectedService[] = [];
      servicesReschedule.forEach((service: IRescheduleServices) => {
        const serviceInfo = allServices.find(
          (item) => item.productId === service.serviceId
        );

        const staffInfo = staffs.find(
          (staff) => staff.staffId === service.staffId
        );

        if (!serviceInfo || !staffInfo) return;

        const _service: ISelectedService = {
          serviceId: service.serviceId,
          serviceName: serviceInfo.productName,
          servicePrice: serviceInfo.price,
          bufferTime: serviceInfo.bufferTime || 0,
          time: serviceInfo.time,
          staffId: service.staffId,
          staffName: staffInfo.staffName,
        };

        _selectedServices.push(_service);
      });
      setSelectedServices(_selectedServices);
    }
  }, [categories, staffs]);

  return (
    <Box className={classes.root}>
      <Box className={classes.container}>
        {isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <RICHLoading />
          </Box>
        ) : (
          <>
            <Box className={classes.header}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                height="65px"
                gap={1}
              >
                <Button
                  onClick={() => handleSwitchTab(activeTab - 1)}
                  variant="text"
                  sx={{
                    minWidth: "unset",
                    width: 28,
                    padding: 0,
                    transform: `translateX(${
                      (activeTab > 0 || serviceStep === 1) &&
                      !bookingForm.submitted
                        ? 0
                        : "-120%"
                    })`,
                    opacity: activeTab > 0 || serviceStep === 1 ? 1 : 0,
                    transition: "0.25s ease-in-out",
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 28, color: "#fff" }} />
                </Button>
                <Box flexGrow={1}>
                  <Typography
                    className={classes.tenantName}
                    fontSize={Math.max(
                      16,
                      Math.min(24, 24 - 0.2 * (tenantName.length - 20))
                    )}
                  >
                    {tenantName}
                  </Typography>
                </Box>
              </Stack>
              {!bookingForm.submitted ? (
                !isBusinessWorkHoursOff ? (
                  <Stack className={classes.stepper} spacing={4}>
                    <Stepper
                      alternativeLabel
                      activeStep={activeTab}
                      connector={<ColorlibConnector />}
                      sx={{ marginTop: "-10px !important" }}
                    >
                      {steps.map((label, index) => (
                        <Step key={label}>
                          <ColorlibStepLabel
                            StepIconComponent={(props) => (
                              <ColorlibStepIcon
                                {...props}
                                onClick={() => {
                                  if (index === 0) setServiceStep(0);
                                  handleSwitchTab(index);
                                }}
                              />
                            )}
                          >
                            <Typography
                              onClick={() => {
                                if (index === 0) setServiceStep(0);
                                handleSwitchTab(index);
                              }}
                              sx={{ cursor: "pointer" }}
                            >
                              {label}
                            </Typography>
                          </ColorlibStepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Stack>
                ) : (
                  <Box className={classes.submittedBox} height="auto">
                    <CalendarNotification />
                  </Box>
                )
              ) : (
                <Box className={classes.submittedBox}>
                  <CalendarCheckIcon />
                  <Typography color="#009045" fontSize={20} fontWeight={600}>
                    Success
                  </Typography>
                </Box>
              )}
            </Box>
            {isBusinessWorkHoursOff ? (
              <Box
                className={classes.contentBox}
                px={3}
                height="calc(100% - 280px)"
              >
                <Typography mt={2} textAlign="center" fontSize={17}>
                  We’d like to inform you that
                  <Typography component="span" fontWeight={600} color="#0B70E1">
                    &nbsp;{tenantName}&nbsp;
                  </Typography>
                  will be closed today. We apologize for any inconvenience this
                  may cause and truly appreciate your understanding.
                </Typography>
                <br />
                <Typography textAlign="center" fontSize={17}>
                  Thank you for your continued support! We look forward to
                  welcoming you back.
                </Typography>
              </Box>
            ) : (
              <>
                {bookingForm.submitted ? (
                  <Box className={classes.contentBox} position="relative">
                    <Box px={2} height="100%">
                      <Typography
                        color="#061340"
                        textAlign="center"
                        textTransform="capitalize"
                      >
                        You can double check your booking information on your
                        phone
                      </Typography>
                      <Box
                        width="100%"
                        height="calc(100% - 150px)"
                        overflow="auto"
                        mt={3}
                        sx={{ animation: "0.75s slide-left" }}
                      >
                        <ServicesInformation
                          date={selectedDateTime.date}
                          services={selectedServices}
                          unixPickDateTime={unixPickDateTime}
                          isBooked
                          includeBufferTime={bookingSettings.includeBufferTime}
                        />
                      </Box>
                      <Box className={classes.bottomBox}>
                        <Button
                          className={classes.backButton}
                          onClick={handleBackToMenu}
                          variant="outlined"
                          color="success"
                        >
                          Back Menu
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box className={classes.contentBox} ref={tabContentRef}>
                    {activeTab === 0 ? (
                      <PickServiceAndStaff
                        categories={categories}
                        staffs={staffs}
                        setActiveTab={setActiveTab}
                        selectedServices={selectedServices}
                        setSelectedServices={setSelectedServices}
                        bookingSettings={bookingSettings}
                        step={serviceStep}
                        setStep={setServiceStep}
                      />
                    ) : activeTab === 1 ? (
                      <PickDayAndTime
                        selectedServices={selectedServices}
                        setActiveTab={setActiveTab}
                        selectedDateTime={selectedDateTime}
                        setSelectedDateTime={setSelectedDateTime}
                        timezone={timezone}
                        bookingSettings={bookingSettings}
                      />
                    ) : (
                      <Confirm
                        selectedServices={selectedServices}
                        setSelectedServices={setSelectedServices}
                        selectedDateTime={selectedDateTime}
                        bookingForm={bookingForm}
                        setBookingForm={setBookingForm}
                        isValidForm={isValidForm}
                        customerInfo={customerInfo}
                        setCustomerInfo={setCustomerInfo}
                        timezone={timezone}
                        unixPickDateTime={unixPickDateTime}
                        bookingSettings={bookingSettings}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
            <Box position="absolute" bottom={6} left={0} width="100%">
              <Typography fontSize={12} color="#C7C7C7" textAlign="center">
                POWERED BY RICH Payment Solutions
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Booking;
